/**
 * PSP/CRE Tool v2.1 - Ambulance Types Constants
 * 
 * Defines ground ambulance specifications for medical evacuation planning.
 * Includes Romanian, NATO, and US standard ambulance types.
 * 
 * @fileoverview Ground ambulance specifications and evacuation capacity
 * @module constants/ambulanceTypes
 * @author Dr. Tatar Radu MD
 * @date December 22, 2024
 * @version 2.7 - BUG-155: Piranha V MEDEVAC added for Romanian fleet (March 18, 2026)
 *   ROOT CAUSE: Romanian brigade fleet showed only TABC79 + MLVM. Per 227-vehicle
 *   GDELS contract (Jan 2018, deliveries from Oct 2020), Romanian Army operates
 *   Piranha V in 6 configurations including armored ambulance.
 *   NEW: PIRANHA_V_MEDEVAC export constant (8×8, STANAG 4, 2 litter)
 *     Manufacturer: GDELS / UMB (Uzina Mecanică București joint production)
 *     Stretcher capacity: 2 litter (based on Piranha IIIC ambulance datasheet, Frestems)
 *     Protection: STANAG 4 (14.5mm AP 360°, 30mm AP frontal)
 *     Status: ACTIVE — first deliveries Oct 2020; ongoing production at UMB
 *     Source: Wikipedia Mowag Piranha V; Army-Technology.com; ELMET International 2022
 *   UPDATED: GROUND_FLEET_BY_NATION.ROMANIAN adds PIRANHA_V_MEDEVAC (count:4, available:4)
 *     reflecting transitional fleet state (March 2026): Piranha V ambulances in service
 *     alongside legacy TABC79 + MLVM; counts unchanged for legacy vehicles.
 *   UPDATED: AMBULANCE_BY_NATIONALITY.ROMANIAN, AMBULANCE_CATEGORIES.ARMORED/ACTIVE
 *   NOTE: FLAG_ICONS import added to avoid bare emoji in new entry.
 *   NOTE: Problem A (fleet not aggregated per brigade TO&E) reclassified as ENH-027
 *     (architectural enhancement, not a bug). Current single-fleet design is intentional.
 * @version 2.6 - FRENCH accuracy fixes (March 15, 2026)
 *   BUG FIX: GROUND_FLEET_BY_NATION.FRENCH label/comment: "Griffon BASALT" → "Griffon SAN".
 *     "BASALT" does not appear in French procurement documentation. Correct name is
 *     "Griffon SAN" (sanitaire variant of the VBMR Griffon). Source: DGA, Arquus, ArmyRecognition.
 *   BUG FIX: GRIFFON_MEDEVAC.manufacturer: added Arquus (chassis/driveline). Scorpion
 *     consortium is Nexter / Arquus / Thales (Arquus = former Renault Trucks Defense, rebranded 2019).
 *   BUG FIX: GRIFFON_MEDEVAC.educational.doctrinal_note: "Delivering 2022 onward" →
 *     first deliveries confirmed 2023 (DGA qualification 2022, first unit deliveries 2023).
 *     196 SAN variants planned by 2030. Source: Eurosatory 2024 / ArmyRecognition June 2024.
 * @version 2.5 - GROUND_FLEET_BY_NATION: Doctrinal accuracy pass + new-flag warning (March 6, 2026)
 *
 *   ROMANIAN (confirmed by Dr. Tatar Radu MD — authoritative):
 *     Old: TABC79={count:12}, MLVM={count:6} — wrong count, wrong split.
 *     Doctrinal structure:
 *       R1 organic: 3 battalions × (2 TABC79 + 2 MLVM) = 6 TABC79 + 6 MLVM at battalion level
 *       R2B medical company: ~8 TABC79 + 4 MLVM (brigade medical company, ~12 total)
 *       Brigade total: TABC79=14, MLVM=10, grand total=24 authorized
 *     Available ~85%: TABC79=12, MLVM=9
 *     TODO (future session): split into R1_organic vs R2B_company breakdown tables
 *     per-battalion view needed for per-echelon capacity planning.
 *
 *   US: Updated to reflect 2024 ABCT modernization (Army Recognition, May 2024).
 *     AMPV (M1284 MEV / M1285 MTV) replacing M113 in ABCTs. M997 legacy still present.
 *     Separate IBCT and ABCT fleet profiles added with conditional note.
 *     Using combined ABCT profile as US default (most relevant for NATO LSCO context).
 *
 *   GERMAN: Updated label to reflect Lkw UVT replacement programme.
 *     Bundeswehr replacing KrKw (Unimog) with IVECO Eurocargo UVT — 294 first tranche
 *     delivered 2023-2027 (BAAINBw July 2021). SPRINTER still primary light ambulance.
 *     Boxer ambulance variant fielded in armored units.
 *
 *   Other nations (POLISH, BRITISH, FRENCH, NATO, MULTINATIONAL):
 *     Numbers reviewed — no corrections required. Doctrinal source notes added inline.
 *
 *   !! MANDATORY — ADDING A NEW NATIONAL FLAG REQUIRES TWO FILES !!
 *   (1) THIS FILE — ambulanceTypes.js:
 *       a. Add vehicle constants to AMBULANCE_TYPES if platform not already present
 *       b. Add nationality to AMBULANCE_BY_NATIONALITY
 *       c. Add entry to GROUND_FLEET_BY_NATION (triggers Tab3 pre-population)
 *   (2) docs/documentation/APP_RULES.md Section 15 — add all mandatory checklist items for the new nation.
 *   Missing either step produces silent wrong results — wrong fleet defaults at brigade
 *   level or a missing nation in the dropdown without a crash.
 *
 * @version 2.4 - National accuracy: Polish, British, French vehicles + GROUND_FLEET_BY_NATION (March 5, 2026)
 *   NEW VEHICLES: STAR_266_AMBULANCE (Polish light), ROSOMAK_MEDIC (Polish armored),
 *   LAND_ROVER_WOLF (British light legacy), FOXHOUND_CASEVAC (British protected),
 *   VAB_MEDEVAC (French armored), GRIFFON_MEDEVAC (French modern fielding).
 *   NEW CONSTANT: GROUND_FLEET_BY_NATION — brigade-level default fleet by national standard.
 *   AMBULANCE_BY_NATIONALITY: extended to POLISH, BRITISH (alias UK), FRENCH, NATO.
 *   NEW EXPORT: getDefaultGroundFleet(nationalStandard) — returns default fleet for Tab3 3B.0.
 *   Adding a new nation requires ONLY a new entry in GROUND_FLEET_BY_NATION + AMBULANCE_BY_NATIONALITY.
 * @version 2.3 - F-04: Add GROUND_AMBULANCE_NTM by category (March 2, 2026)
 *
 * VERSION 2.2 CHANGES (February 22, 2026) - EXT-03:
 * - ADD: `CONVOY_TACTICAL_SPEED_KMH = 30` — authoritative base speed for ground
 *   medical convoy planning. Replaces hardcoded `30` literals in:
 *   `supplyChainModule.js` (SCM-07, Phase 3), `methodBasedAssetsModule.js` (MAM-07, Phase 3),
 *   `transportCalculator.js` (P1-08, already uses TRANSPORT_SPECS; TC-01 consolidates further).
 *   Placed after modifier constants — callers multiply by ROAD_CONDITION_MODIFIERS or
 *   WEATHER_MODIFIERS to get effective convoy speed.
 * - Removed: Romanian Military Medical Institute from @author (APP_RULES 5.3).
 *
 * VERSION 2.3 CHANGES (March 2, 2026) - F-04 / ARCH-011:
 * - ADD: `GROUND_AMBULANCE_NTM` — Notice To Move times by ambulance category.
 *   Ground ambulance equivalent of helicopter NTM (helicopterSpecs.js v2.3).
 *   Doctrinal basis: FM 4-02.4 ground MEDEVAC crew readiness + NATO STANAG 2026.
 *   Placed after CONVOY_TACTICAL_SPEED_KMH — same modifiers domain.
 * - ADD: `ntm_minutes` field to CATEGORY_DEFAULTS inside getAmbulanceCalcSpec().
 *   New categories added: PROTECTED and HEAVY_ARMORED (previously fell through to WHEELED default).
 * - ADD: `ntm_minutes` to getAmbulanceCalcSpec() return object.
 *   Callers (medevacModule, methodBasedAssetsModule) receive NTM automatically via
 *   getAmbulanceCalcSpec() — no per-vehicle maintenance required.
 *   When Romania replaces TABC-79 with Piranha (ARMORED category), NTM is inherited
 *   from the category table without any additional field on the new vehicle entry.
 * 
 * Categories:
 * - LIGHT: Wheeled, road-optimized, higher patient capacity, minimal protection
 * - PROTECTED: Wheeled, MRAP-level protection, good mobility (JLTV class)
 * - ARMORED: Tracked/wheeled, good protection, lower capacity
 * - HEAVY_ARMORED: Heavy tracked, excellent protection (AMPV class)
 * - MRAP: Mine-resistant, excellent IED protection, limited capacity
 * 
 * VERSION 2.1 CHANGES:
 * - icons.js integration (TRANSPORT_ICONS, STATUS_ICONS)
 * - Added getAmbulanceOptions() with full metadata for UI
 * - Added getCategoryOptions(), getSTANAGLevelOptions()
 * - Added validateAmbulanceConfig() function
 * - Enhanced educational metadata
 * - Added Ukraine 2022-2024 lessons
 */

// ============================================================================
// ICONS IMPORT
// ============================================================================

import {
  STATUS_ICONS,
  TRANSPORT_ICONS,
  TACTICAL_ICONS,
  FLAG_ICONS,          // BUG-155: needed for PIRANHA_V_MEDEVAC flag field
} from '../utils/icons';

// ============================================================================
// ICON DEFINITIONS (Fallback if icons.js not available)
// ============================================================================

const ICONS = {
  // Status icons
  GREEN: STATUS_ICONS?.GREEN || 'ðŸŸ¢',
  YELLOW: STATUS_ICONS?.YELLOW || 'ðŸŸ¡',
  RED: STATUS_ICONS?.RED || 'ðŸ”´',
  ORANGE: STATUS_ICONS?.ORANGE || 'ðŸŸ ',
  
  // Transport icons
  AMBULANCE: TRANSPORT_ICONS?.AMBULANCE || 'ðŸš‘',
  VEHICLE: TRANSPORT_ICONS?.VEHICLE || 'ðŸš—',
  TANK: TRANSPORT_ICONS?.TANK || 'ðŸ›¡ï¸',
  
  // Tactical icons
  WARNING: TACTICAL_ICONS?.WARNING || 'âš ï¸',
  SHIELD: TACTICAL_ICONS?.SHIELD || 'ðŸ›¡ï¸'
};

// ============================================================================
// STANAG 4569 PROTECTION LEVELS
// ============================================================================

/**
 * NATO STANAG 4569 - Protection Levels for Logistic and Light Armored Vehicles
 */
export const STANAG_PROTECTION_LEVELS = {
  LEVEL_0: {
    level: 0,
    description: 'No ballistic protection',
    icon: ICONS.RED,
    kinetic_threat: 'None',
    artillery_fragments: 'None',
    mine_protection: 'None'
  },
  LEVEL_1: {
    level: 1,
    description: 'Protection against 7.62x51mm NATO Ball at 30m',
    icon: ICONS.ORANGE,
    kinetic_threat: '7.62x51mm Ball',
    artillery_fragments: '155mm at 100m',
    mine_protection: 'Hand grenades under vehicle'
  },
  LEVEL_2: {
    level: 2,
    description: 'Protection against 7.62x39mm API BZ at 30m',
    icon: ICONS.YELLOW,
    kinetic_threat: '7.62x39mm API',
    artillery_fragments: '155mm at 80m',
    mine_protection: '6kg AT mine under wheel'
  },
  LEVEL_3: {
    level: 3,
    description: 'Protection against 7.62x51mm AP at 30m',
    icon: ICONS.YELLOW,
    kinetic_threat: '7.62x51mm AP',
    artillery_fragments: '155mm at 60m',
    mine_protection: '8kg AT mine under wheel'
  },
  LEVEL_4: {
    level: 4,
    description: 'Protection against 14.5x114mm API at 200m',
    icon: ICONS.GREEN,
    kinetic_threat: '14.5mm API',
    artillery_fragments: '155mm at 25m',
    mine_protection: '10kg AT mine under belly'
  },
  LEVEL_5: {
    level: 5,
    description: 'Protection against 25mm APDS at 500m',
    icon: ICONS.GREEN,
    kinetic_threat: '25mm APDS',
    artillery_fragments: '155mm airburst',
    mine_protection: '10kg AT mine under belly'
  },
  LEVEL_6: {
    level: 6,
    description: 'Protection against 30mm APFSDS at 500m',
    icon: ICONS.GREEN,
    kinetic_threat: '30mm APFSDS',
    artillery_fragments: '155mm airburst',
    mine_protection: '10kg AT mine under belly'
  }
};

// ============================================================================
// DRONE THREAT MODIFIERS (Ukraine 2022-2024 Lessons)
// ============================================================================

/**
 * Drone threat significantly impacts ground evacuation in modern warfare.
 * Ukraine data shows 18% of casualties from drone strikes (2024).
 */
export const DRONE_THREAT_MODIFIERS = {
  NONE: {
    code: 'NONE',
    description: 'No drone threat',
    icon: ICONS.GREEN,
    speed_modifier: 1.0,
    route_modifier: 1.0,
    movement_restriction: 'NONE',
    daylight_risk: 'LOW',
    recommended_tactics: 'Normal operations'
  },
  LOW: {
    code: 'LOW',
    description: 'Occasional reconnaissance drones',
    icon: ICONS.GREEN,
    speed_modifier: 0.95,
    route_modifier: 1.1,
    movement_restriction: 'AVOID_OPEN_TERRAIN',
    daylight_risk: 'LOW',
    recommended_tactics: 'Use terrain for concealment when possible'
  },
  MEDIUM: {
    code: 'MEDIUM',
    description: 'Regular drone surveillance, occasional FPV attacks',
    icon: ICONS.YELLOW,
    speed_modifier: 0.85,
    route_modifier: 1.3,
    movement_restriction: 'USE_CONCEALMENT',
    daylight_risk: 'MODERATE',
    recommended_tactics: 'Use tree lines, buildings. Disperse vehicles. Brief halts only.'
  },
  HIGH: {
    code: 'HIGH',
    description: 'Persistent drone threat, frequent FPV attacks',
    icon: ICONS.ORANGE,
    speed_modifier: 0.70,
    route_modifier: 1.5,
    movement_restriction: 'MOVEMENT_WINDOWS_ONLY',
    daylight_risk: 'HIGH',
    recommended_tactics: 'Move during weather windows. EW support recommended. Armored vehicles only forward.',
    educational: {
      scenario: 'Bakhmut 2023 conditions',
      key_point: '30% speed reduction expected'
    }
  },
  EXTREME: {
    code: 'EXTREME',
    description: 'Continuous drone surveillance, immediate FPV response',
    icon: ICONS.RED,
    speed_modifier: 0.50,
    route_modifier: 2.0,
    movement_restriction: 'NIGHT_MOVEMENT_ONLY',
    daylight_risk: 'CRITICAL',
    recommended_tactics: 'Night movement only. Full EW support required. Consider alternative evacuation methods.',
    educational: {
      scenario: 'Active drone swarm environment',
      key_point: '50% capacity reduction - night ops essential'
    }
  }
};

// ============================================================================
// NIGHT OPERATIONS MODIFIERS
// ============================================================================

export const NIGHT_OPERATIONS_MODIFIERS = {
  LIGHT: {
    category: 'LIGHT',
    icon: ICONS.YELLOW,
    speed_modifier: 0.60,
    capacity_modifier: 1.0,
    nvg_required: true,
    thermal_capable: false,
    accident_risk_increase: 0.25,
    notes: 'Major speed reduction. High accident risk without NVG training.'
  },
  PROTECTED: {
    category: 'PROTECTED',
    icon: ICONS.GREEN,
    speed_modifier: 0.80,
    capacity_modifier: 1.0,
    nvg_required: true,
    thermal_capable: true,
    accident_risk_increase: 0.10,
    notes: 'Good night capability with driver thermal viewer.'
  },
  ARMORED: {
    category: 'ARMORED',
    icon: ICONS.YELLOW,
    speed_modifier: 0.75,
    capacity_modifier: 1.0,
    nvg_required: true,
    thermal_capable: false,
    accident_risk_increase: 0.15,
    notes: 'Moderate speed reduction. Thermal upgrade available.'
  },
  HEAVY_ARMORED: {
    category: 'HEAVY_ARMORED',
    icon: ICONS.GREEN,
    speed_modifier: 0.80,
    capacity_modifier: 1.0,
    nvg_required: true,
    thermal_capable: true,
    accident_risk_increase: 0.08,
    notes: 'Good night capability with integrated thermal systems.'
  },
  MRAP: {
    category: 'MRAP',
    icon: ICONS.GREEN,
    speed_modifier: 0.75,
    capacity_modifier: 1.0,
    nvg_required: true,
    thermal_capable: true,
    accident_risk_increase: 0.12,
    notes: 'Good night capability. Height increases rollover risk.'
  }
};

// ============================================================================
// EXTENDED EVACUATION EFFECTS (Ukraine 2022-2024 Data)
// ============================================================================

export const EXTENDED_EVACUATION_EFFECTS = {
  mortality_increase_per_hour: {
    T1_IMMEDIATE: 0.08,
    T2_DELAYED: 0.03,
    T3_MINIMAL: 0.01,
    T4_EXPECTANT: 0.00
  },
  
  required_capability: {
    under_30min: {
      level: 'BASIC',
      icon: ICONS.GREEN,
      description: 'Standard first aid capability sufficient',
      blood_required: false,
      ventilator_required: false
    },
    under_1h: {
      level: 'BASIC',
      icon: ICONS.GREEN,
      description: 'Standard first aid capability',
      blood_required: false,
      ventilator_required: false
    },
    '1h_to_2h': {
      level: 'INTERMEDIATE',
      icon: ICONS.YELLOW,
      description: 'Enhanced monitoring capability',
      blood_required: 'T1_ONLY',
      ventilator_required: false
    },
    '2h_to_4h': {
      level: 'ADVANCED',
      icon: ICONS.ORANGE,
      description: 'PFC capability required',
      blood_required: true,
      ventilator_required: true
    },
    over_4h: {
      level: 'PFC',
      icon: ICONS.RED,
      description: 'Full prolonged field care',
      blood_required: true,
      ventilator_required: true
    }
  }
};

// ============================================================================
// ROAD CONDITION MODIFIERS
// ============================================================================

export const ROAD_CONDITION_MODIFIERS = {
  PAVED_GOOD: {
    code: 'PAVED_GOOD',
    description: 'Good paved roads',
    icon: ICONS.GREEN,
    speed_modifier: 1.0,
    fuel_modifier: 1.0
  },
  PAVED_DAMAGED: {
    code: 'PAVED_DAMAGED',
    description: 'Damaged paved roads (shell craters, debris)',
    icon: ICONS.YELLOW,
    speed_modifier: 0.70,
    fuel_modifier: 1.15
  },
  UNPAVED_DRY: {
    code: 'UNPAVED_DRY',
    description: 'Unpaved roads - dry conditions',
    icon: ICONS.YELLOW,
    speed_modifier: 0.60,
    fuel_modifier: 1.25
  },
  UNPAVED_WET: {
    code: 'UNPAVED_WET',
    description: 'Unpaved roads - wet/muddy',
    icon: ICONS.ORANGE,
    speed_modifier: 0.40,
    fuel_modifier: 1.50
  },
  CROSS_COUNTRY: {
    code: 'CROSS_COUNTRY',
    description: 'Cross-country movement',
    icon: ICONS.RED,
    speed_modifier: 0.30,
    fuel_modifier: 2.0,
    requires: 'OFF_ROAD_CAPABLE'
  }
};

// ============================================================================
// WEATHER MODIFIERS
// ============================================================================

export const WEATHER_MODIFIERS = {
  CLEAR: {
    code: 'CLEAR',
    description: 'Clear weather',
    icon: 'â˜€ï¸',
    speed_modifier: 1.0,
    visibility_modifier: 1.0
  },
  RAIN_LIGHT: {
    code: 'RAIN_LIGHT',
    description: 'Light rain',
    icon: 'ðŸŒ§ï¸',
    speed_modifier: 0.90,
    visibility_modifier: 0.85
  },
  RAIN_HEAVY: {
    code: 'RAIN_HEAVY',
    description: 'Heavy rain',
    icon: 'ðŸŒ§ï¸',
    speed_modifier: 0.70,
    visibility_modifier: 0.60
  },
  SNOW: {
    code: 'SNOW',
    description: 'Snow conditions',
    icon: 'â„ï¸',
    speed_modifier: 0.50,
    visibility_modifier: 0.50
  },
  FOG: {
    code: 'FOG',
    description: 'Fog/low visibility',
    icon: 'ðŸŒ«ï¸',
    speed_modifier: 0.40,
    visibility_modifier: 0.20
  }
};

// ============================================================================
// CONVOY_TACTICAL_SPEED_KMH
// EXT-03: Base ground convoy speed for medical logistics and evacuation planning.
// Single authoritative source replacing hardcoded `30` literals across modules.
//
// Apply environmental modifiers to get effective convoy speed:
//   effective_speed = CONVOY_TACTICAL_SPEED_KMH
//                     × ROAD_CONDITION_MODIFIERS[road_type].speed_modifier
//                     × WEATHER_MODIFIERS[weather].speed_modifier
//                     × NIGHT_OPERATIONS_MODIFIERS[nod_capability].speed_modifier
//
// Consumed by (Phase 3 fixes):
//   supplyChainModule.js   SCM-07: calculateEvacuationCapacity()
//   methodBasedAssetsModule.js MAM-07: ground evacuation distance/time
//   transportCalculator.js  already using TRANSPORT_SPECS.GROUND.MEDICAL_5TON.speed_kph
//                            which must be kept in sync with this value
//
// Reference: ATP 4-02.2 Medical Evacuation; NATO STANAG 2228;
//   Romania Army tactical convoy planning doctrine
// ============================================================================

export const CONVOY_TACTICAL_SPEED_KMH = 30; // kph — standard tactical road convoy

// ============================================================================
// F-04 / ARCH-011: GROUND AMBULANCE NOTICE TO MOVE (NTM) BY CATEGORY
// Crew alert + vehicle preparation time before the vehicle is rolling.
// Ground equivalent of helicopter NTM (helicopterSpecs.js v2.3 ARCH-011).
//
// Doctrinal basis:
//   FM 4-02.4: Ground MEDEVAC crew readiness standards
//   NATO STANAG 2026: Medical unit readiness levels
//
// Maintenance rule (APP_RULES 2.1 — constants-based architecture):
//   When a nation replaces one vehicle with another of the SAME category,
//   NTM is inherited automatically. No per-vehicle field needed.
//   When adding a NEW CATEGORY, add the entry here — one change, all vehicles.
//
// Category rationale:
//   LIGHT (5 min):        Unprotected wheeled — fast engine start, minimal PMCS.
//                         TABC-79, Sprinter, M997.
//   PROTECTED (8 min):    Protected wheeled — PMCS checks, MRAP pre-departure.
//                         JLTV class.
//   MRAP (8 min):         Same readiness class as PROTECTED.
//                         M-ATV.
//   ARMORED (12 min):     Tracked and modern wheeled armored — engine warm-up,
//                         pre-battle checks. M113, MLVM, Stryker MEV, Boxer.
//   HEAVY_ARMORED (10 min): Modern heavy tracked — better electronics-assisted
//                         startup than legacy track (AMPV vs M113).
//   WHEELED (8 min):      Catch-all for unclassified wheeled vehicles.
//   BUS (5 min):          Non-combat, parking-lot ready.
// ============================================================================
export const GROUND_AMBULANCE_NTM = {
  LIGHT:         { ntm_minutes: 5,  description: 'Unprotected wheeled — fast start, minimal PMCS' },
  PROTECTED:     { ntm_minutes: 8,  description: 'Protected wheeled (JLTV class) — PMCS + pre-departure checks' },
  MRAP:          { ntm_minutes: 8,  description: 'Mine-resistant wheeled — same readiness as PROTECTED' },
  ARMORED:       { ntm_minutes: 12, description: 'Tracked/armored wheeled — engine warm-up + pre-battle checks' },
  HEAVY_ARMORED: { ntm_minutes: 10, description: 'Modern heavy tracked (AMPV) — electronics-assisted startup' },
  WHEELED:       { ntm_minutes: 8,  description: 'Generic wheeled — standard pre-departure checks' },
  BUS:           { ntm_minutes: 5,  description: 'Non-combat vehicle — parking-lot ready' },
};

// ============================================================================
// AMBULANCE TYPE DEFINITIONS
// ============================================================================

/**
 * TABC-79 - Romanian Light Ambulance
 */
export const TABC_79 = {
  code: 'TABC79',
  name: 'TABC-79 Ambulance',
  manufacturer: 'ROMAN',
  nationality: 'Romanian',
  flag: 'ðŸ‡·ðŸ‡´',
  icon: ICONS.AMBULANCE,
  category: 'LIGHT',
  
  capacity: {
    stretchers: 4,
    ambulatory: 6,
    mixed_stretchers: 2,
    mixed_ambulatory: 3,
    crew: 2,
    medic: 1
  },
  
  performance: {
    max_speed_kmh: 90,
    cruise_speed_kmh: 70,
    range_km: 600,
    fuel_capacity_liters: 150,
    fuel_consumption_l_per_100km: 25
  },
  
  armor_protection: 'NONE',
  
  survivability: {
    stanag_level: 0,
    ied_survival_rate: 0.30,
    small_arms_protection: false,
    mine_protection: false
  },
  
  medical_equipment: {
    oxygen_system: true,
    suction: true,
    monitoring: 'BASIC',
    prolonged_care_capable: false
  },
  
  fielding_status: 'LEGACY',
  
  educational: {
    key_point: 'Romanian standard - no armor protection',
    limitation: 'Not suitable for contested environment',
    recommendation: 'Use in rear areas only'
  }
};

// ============================================================================
// PIRANHA V MEDEVAC - Romanian Armored (BUG-155)
// ============================================================================
/**
 * Piranha V MEDEVAC - Romanian Army armored ambulance variant
 *
 * Platform: GDELS Mowag Piranha V 8×8 wheeled APC
 * Contract: 227 vehicles in 6 configurations, signed Jan 2018 (GDELS/Romania).
 *   Configurations include: IFV, command, ambulance, mortar, CBRN, recovery.
 *   First batch of 36 delivered Oct 2020; ongoing production at UMB, Bucharest.
 *   150 additional vehicles announced Feb 2023 (~USD 674M).
 * Stretcher capacity: 2 litter (Piranha IIIC ambulance datasheet baseline;
 *   Piranha V has greater internal volume but no public spec confirms higher count).
 * Protection: STANAG 4 (14.5mm AP 360°, 30mm AP frontal arc, 8kg AT mine).
 * Source: Wikipedia Mowag Piranha V; Army-Technology Piranha 5; ELMET 2022.
 */
export const PIRANHA_V_MEDEVAC = {
  code: 'PIRANHA_V_MEDEVAC',
  name: 'Piranha V MEDEVAC',
  manufacturer: 'GDELS / UMB (General Dynamics European Land Systems / Uzina Mecanică București)',
  nationality: 'Romanian',
  flag: FLAG_ICONS.ROMANIAN,
  icon: ICONS.AMBULANCE,
  category: 'ARMORED',

  capacity: {
    stretchers: 2,        // Piranha IIIC = 2 primary + 1 provisional; V larger but unconfirmed
    ambulatory: 4,
    crew: 2,
    medic: 1
  },

  performance: {
    max_speed_kmh: 100,
    cruise_speed_kmh: 80,
    range_km: 550,
    fuel_consumption_l_per_100km: 45
  },

  armor_protection: 'STANAG_4',

  survivability: {
    stanag_level: 4,
    ied_survival_rate: 0.75,
    small_arms_protection: true,
    mine_protection: true        // 8kg AT mine per GDELS Piranha V specification
  },

  medical_equipment: {
    oxygen_system: true,
    suction: true,
    monitoring: 'STANDARD',
    climate_control: true,
    prolonged_care_capable: false
  },

  fielding_status: 'ACTIVE',

  educational: {
    key_point: 'Romanian Army armored ambulance — Piranha V 8×8 platform, STANAG 4 protection',
    advantage: 'Full STANAG 4 protection; produced locally at UMB Bucharest; superior to MLVM in contested environments',
    limitation: '2-litter capacity limits simultaneous T1 transport vs MLVM TAB-77 (4-litter)',
    best_use: 'Forward armored MEDEVAC in high-threat sectors; Role 1 to Role 2B evacuation under fire',
    doctrinal_note: '227-vehicle contract signed Jan 2018 with GDELS. Ambulance is 1 of 6 variants. ' +
      'First batch delivered Oct 2020 (26th Inf Bn "Neagoe Basarab" / Red Scorpions). ' +
      'Production ongoing at UMB (Uzina Mecanică București) joint venture. ' +
      'Additional 150 vehicles announced Feb 2023. Source: Wikipedia Mowag Piranha V; ' +
      'GDELS press releases; ELMET International Oct 2022; Army Recognition 2024.'
  }
};

/**
 * M997 HMMWV Ambulance - US Legacy
 */
export const M997_HMMWV = {
  code: 'M997',
  name: 'M997 HMMWV Ambulance',
  manufacturer: 'AM General',
  nationality: 'US',
  flag: 'ðŸ‡ºðŸ‡¸',
  icon: ICONS.AMBULANCE,
  category: 'LIGHT',
  
  capacity: {
    stretchers: 4,
    ambulatory: 8,
    crew: 2,
    medic: 1
  },
  
  performance: {
    max_speed_kmh: 113,
    cruise_speed_kmh: 88,
    range_km: 480,
    fuel_capacity_liters: 95,
    fuel_consumption_l_per_100km: 20
  },
  
  armor_protection: 'NONE',
  
  survivability: {
    stanag_level: 0,
    ied_survival_rate: 0.35,
    small_arms_protection: false,
    mine_protection: false
  },
  
  medical_equipment: {
    oxygen_system: true,
    suction: true,
    monitoring: 'BASIC',
    prolonged_care_capable: false
  },
  
  fielding_status: 'LEGACY',
  
  educational: {
    key_point: 'Being replaced by JLTV',
    limitation: 'Very vulnerable to IEDs - 35% survival',
    ukraine_lesson: 'High loss rate in Ukraine'
  }
};

/**
 * Mercedes Sprinter Ambulance - Civilian/Military
 */
export const SPRINTER_AMBULANCE = {
  code: 'SPRINTER',
  name: 'Mercedes Sprinter Ambulance',
  manufacturer: 'Mercedes-Benz',
  nationality: 'German',
  flag: 'ðŸ‡©ðŸ‡ª',
  icon: ICONS.AMBULANCE,
  category: 'LIGHT',
  
  capacity: {
    stretchers: 2,
    ambulatory: 4,
    crew: 2,
    medic: 1
  },
  
  performance: {
    max_speed_kmh: 160,
    cruise_speed_kmh: 120,
    range_km: 500,
    fuel_capacity_liters: 100,
    fuel_consumption_l_per_100km: 12
  },
  
  armor_protection: 'NONE',
  
  survivability: {
    stanag_level: 0,
    ied_survival_rate: 0.25,
    small_arms_protection: false
  },
  
  medical_equipment: {
    oxygen_system: true,
    suction: true,
    monitoring: 'ENHANCED',
    prolonged_care_capable: true
  },
  
  fielding_status: 'ACTIVE',
  
  educational: {
    key_point: 'Best medical equipment, worst protection',
    best_use: 'Rear area evacuation, Role 2â†’3',
    limitation: 'NO combat zone use'
  }
};

/**
 * JLTV Ambulance (M1286) - US Current
 */
export const JLTV_AMBULANCE = {
  code: 'JLTV',
  name: 'JLTV Ambulance (M1286)',
  manufacturer: 'Oshkosh Defense',
  nationality: 'US',
  flag: 'ðŸ‡ºðŸ‡¸',
  icon: ICONS.AMBULANCE,
  category: 'PROTECTED',
  
  capacity: {
    stretchers: 2,
    ambulatory: 4,
    crew: 2,
    medic: 1
  },
  
  performance: {
    max_speed_kmh: 112,
    cruise_speed_kmh: 88,
    range_km: 480,
    fuel_capacity_liters: 227,
    fuel_consumption_l_per_100km: 30
  },
  
  armor_protection: 'STANAG_4',
  
  survivability: {
    stanag_level: 4,
    ied_survival_rate: 0.75,
    small_arms_protection: true,
    mine_protection: true,
    underbelly_protection: true
  },
  
  medical_equipment: {
    oxygen_system: true,
    suction: true,
    monitoring: 'ENHANCED',
    prolonged_care_capable: true
  },
  
  fielding_status: 'ACTIVE',
  
  educational: {
    key_point: 'HMMWV replacement - much better protection',
    advantage: '75% IED survival vs 35% for HMMWV',
    limitation: 'Only 2 stretchers'
  }
};

/**
 * M-ATV Ambulance - MRAP
 */
export const M_ATV_AMBULANCE = {
  code: 'MATV',
  name: 'M-ATV Ambulance',
  manufacturer: 'Oshkosh Defense',
  nationality: 'US',
  flag: 'ðŸ‡ºðŸ‡¸',
  icon: ICONS.AMBULANCE,
  category: 'MRAP',
  
  capacity: {
    stretchers: 2,
    ambulatory: 4,
    crew: 2,
    medic: 1
  },
  
  performance: {
    max_speed_kmh: 105,
    cruise_speed_kmh: 80,
    range_km: 500,
    fuel_consumption_l_per_100km: 35
  },
  
  armor_protection: 'STANAG_4_PLUS',
  
  survivability: {
    stanag_level: 4,
    ied_survival_rate: 0.85,
    small_arms_protection: true,
    mine_protection: true,
    v_hull: true
  },
  
  medical_equipment: {
    oxygen_system: true,
    suction: true,
    monitoring: 'ENHANCED',
    prolonged_care_capable: true
  },
  
  fielding_status: 'ACTIVE',
  
  educational: {
    key_point: 'Best IED protection in class',
    best_use: 'Forward area evacuation in IED threat',
    limitation: 'High center of gravity - rollover risk'
  }
};

/**
 * M113 Ambulance - Legacy Armored
 */
export const M113_AMBULANCE = {
  code: 'M113',
  name: 'M113 Armored Ambulance',
  manufacturer: 'BAE Systems',
  nationality: 'US',
  flag: 'ðŸ‡ºðŸ‡¸',
  icon: ICONS.AMBULANCE,
  category: 'ARMORED',
  
  capacity: {
    stretchers: 4,
    ambulatory: 6,
    crew: 2,
    medic: 1
  },
  
  performance: {
    max_speed_kmh: 67,
    cruise_speed_kmh: 50,
    range_km: 480,
    fuel_consumption_l_per_100km: 45
  },
  
  armor_protection: 'STANAG_2',
  
  survivability: {
    stanag_level: 2,
    ied_survival_rate: 0.55,
    small_arms_protection: true,
    tracked: true
  },
  
  medical_equipment: {
    oxygen_system: true,
    suction: true,
    prolonged_care_capable: false
  },
  
  fielding_status: 'LEGACY',
  
  educational: {
    key_point: 'Tracked - good cross-country',
    limitation: 'Inadequate mine protection',
    ukraine_lesson: 'High losses to mines/artillery'
  }
};

/**
 * MLVM TAB-77 - Romanian Armored
 */
export const MLVM_TAB77 = {
  code: 'MLVM',
  name: 'MLVM TAB-77',
  manufacturer: 'ROMARM',
  nationality: 'Romanian',
  flag: 'ðŸ‡·ðŸ‡´',
  icon: ICONS.AMBULANCE,
  category: 'ARMORED',
  
  capacity: {
    stretchers: 4,
    ambulatory: 4,
    crew: 2,
    medic: 1
  },
  
  performance: {
    max_speed_kmh: 80,
    cruise_speed_kmh: 60,
    range_km: 550,
    amphibious: true
  },
  
  armor_protection: 'STANAG_2',
  
  survivability: {
    stanag_level: 2,
    ied_survival_rate: 0.50,
    small_arms_protection: true,
    wheeled: true
  },
  
  medical_equipment: {
    oxygen_system: true,
    suction: true,
    prolonged_care_capable: false
  },
  
  fielding_status: 'ACTIVE',
  
  educational: {
    key_point: 'Romanian standard armored ambulance',
    advantage: 'Amphibious capability',
    limitation: 'STANAG 2 only - upgrade needed'
  }
};

/**
 * Stryker MEV - Wheeled Heavy
 */
export const STRYKER_MEV = {
  code: 'STRYKER',
  name: 'Stryker MEV (M1133)',
  manufacturer: 'General Dynamics',
  nationality: 'US',
  flag: 'ðŸ‡ºðŸ‡¸',
  icon: ICONS.AMBULANCE,
  category: 'ARMORED',
  
  capacity: {
    stretchers: 6,
    ambulatory: 4,
    crew: 2,
    medic: 2
  },
  
  performance: {
    max_speed_kmh: 97,
    cruise_speed_kmh: 80,
    range_km: 500,
    fuel_consumption_l_per_100km: 50
  },
  
  armor_protection: 'STANAG_4',
  
  survivability: {
    stanag_level: 4,
    ied_survival_rate: 0.70,
    small_arms_protection: true,
    double_v_hull: true
  },
  
  medical_equipment: {
    oxygen_system: true,
    suction: true,
    monitoring: 'ENHANCED',
    prolonged_care_capable: true
  },
  
  fielding_status: 'ACTIVE',
  
  educational: {
    key_point: 'Best balance of capacity (6) and protection',
    best_use: 'Stryker BCT organic ambulance',
    limitation: 'Large signature'
  }
};

/**
 * AMPV MEV (M1283) - Heavy Armored
 */
export const AMPV_MEV = {
  code: 'AMPV',
  name: 'AMPV MEV (M1283)',
  manufacturer: 'BAE Systems',
  nationality: 'US',
  flag: 'ðŸ‡ºðŸ‡¸',
  icon: ICONS.AMBULANCE,
  category: 'HEAVY_ARMORED',
  
  capacity: {
    stretchers: 6,
    ambulatory: 3,
    crew: 2,
    medic: 2
  },
  
  performance: {
    max_speed_kmh: 61,
    cruise_speed_kmh: 48,
    range_km: 320,
    tracked: true
  },
  
  armor_protection: 'STANAG_5',
  
  survivability: {
    stanag_level: 5,
    ied_survival_rate: 0.85,
    small_arms_protection: true,
    mine_protection: true,
    artillery_protection: true
  },
  
  medical_equipment: {
    oxygen_system: true,
    suction: true,
    monitoring: 'ENHANCED',
    prolonged_care_capable: true
  },
  
  fielding_status: 'FIELDING',
  
  educational: {
    key_point: 'M113 replacement - much better protection',
    advantage: 'STANAG 5 - best armor in class',
    best_use: 'ABCT organic ambulance'
  }
};

/**
 * Boxer Ambulance - NATO Multinational
 */
export const BOXER_AMBULANCE = {
  code: 'BOXER',
  name: 'Boxer Ambulance',
  manufacturer: 'ARTEC',
  nationality: 'German/Netherlands',
  icon: ICONS.AMBULANCE,
  category: 'ARMORED',
  
  capacity: {
    stretchers: 4,
    ambulatory: 6,
    crew: 2,
    medic: 2
  },
  
  performance: {
    max_speed_kmh: 103,
    cruise_speed_kmh: 85,
    range_km: 1000,
    fuel_consumption_l_per_100km: 45
  },
  
  armor_protection: 'STANAG_4',
  
  survivability: {
    stanag_level: 4,
    ied_survival_rate: 0.75,
    small_arms_protection: true,
    mine_protection: true
  },
  
  medical_equipment: {
    oxygen_system: true,
    suction: true,
    monitoring: 'ENHANCED',
    climate_control: true,
    prolonged_care_capable: true
  },
  
  fielding_status: 'ACTIVE',
  
  educational: {
    key_point: 'NATO modular platform — German/Dutch standard. Excellent range and protection.',
    advantage: '1000km range, modular mission module, STANAG 4 protection',
    best_use: 'European NATO forces (Germany, Netherlands, Belgium, Australia)'
  }
};

// ============================================================================
// STAR 266 / HONKER - Polish Light Ambulance
// ============================================================================

export const STAR_266_AMBULANCE = {
  code: 'STAR266',
  name: 'Star 266 Ambulance',
  manufacturer: 'Star Trucks (Poland)',
  nationality: 'Polish',
  icon: ICONS.AMBULANCE,
  category: 'LIGHT',

  capacity: {
    stretchers: 2,
    ambulatory: 6,
    crew: 2,
    medic: 1
  },

  performance: {
    max_speed_kmh: 85,
    cruise_speed_kmh: 70,
    range_km: 400,
    fuel_consumption_l_per_100km: 20
  },

  armor_protection: 'NONE',

  survivability: {
    stanag_level: 0,
    ied_survival_rate: 0.10,
    small_arms_protection: false
  },

  medical_equipment: {
    oxygen_system: true,
    suction: true,
    monitoring: 'BASIC',
    climate_control: false,
    prolonged_care_capable: false
  },

  fielding_status: 'LEGACY',

  educational: {
    key_point: 'Polish legacy light ambulance — being replaced by Mercedes Sprinter and Rosomak derivatives',
    limitation: 'No armor — PERMISSIVE environment only',
    best_use: 'Rear area / Role 1 to Role 2 in permissive conditions'
  }
};

// ============================================================================
// ROSOMAK MEDIC - Polish Armored Ambulance (KTO Rosomak 8x8 based)
// ============================================================================

export const ROSOMAK_MEDIC = {
  code: 'ROSOMAK_MEDIC',
  name: 'Rosomak Medic (KTO 8x8)',
  manufacturer: 'HSW / Rosomak S.A. (Poland)',
  nationality: 'Polish',
  icon: ICONS.AMBULANCE,
  category: 'ARMORED',

  capacity: {
    stretchers: 4,
    ambulatory: 6,
    crew: 2,
    medic: 2
  },

  performance: {
    max_speed_kmh: 100,
    cruise_speed_kmh: 80,
    range_km: 800,
    fuel_consumption_l_per_100km: 35
  },

  armor_protection: 'STANAG_3',

  survivability: {
    stanag_level: 3,
    ied_survival_rate: 0.60,
    small_arms_protection: true,
    mine_protection: true
  },

  medical_equipment: {
    oxygen_system: true,
    suction: true,
    monitoring: 'STANDARD',
    climate_control: true,
    prolonged_care_capable: true
  },

  fielding_status: 'ACTIVE',

  educational: {
    key_point: 'Polish standard armored ambulance — derivative of the Rosomak IFV platform',
    advantage: 'High mobility, good protection, interoperable with Polish Land Forces IFV fleet',
    best_use: 'Role 1 to Role 2 in contested environments within Polish formations'
  }
};

// ============================================================================
// LAND ROVER WOLF / DEFENDER AMBULANCE - British Light
// ============================================================================

export const LAND_ROVER_WOLF = {
  code: 'LAND_ROVER_WOLF',
  name: 'Land Rover Wolf / Defender Ambulance',
  manufacturer: 'Land Rover (UK)',
  nationality: 'British',
  icon: ICONS.AMBULANCE,
  category: 'LIGHT',

  capacity: {
    stretchers: 2,
    ambulatory: 4,
    crew: 2,
    medic: 1
  },

  performance: {
    max_speed_kmh: 120,
    cruise_speed_kmh: 90,
    range_km: 500,
    fuel_consumption_l_per_100km: 15
  },

  armor_protection: 'NONE',

  survivability: {
    stanag_level: 0,
    ied_survival_rate: 0.05,
    small_arms_protection: false
  },

  medical_equipment: {
    oxygen_system: true,
    suction: true,
    monitoring: 'BASIC',
    climate_control: false,
    prolonged_care_capable: false
  },

  fielding_status: 'LEGACY',

  educational: {
    key_point: 'British legacy light ambulance — being replaced by Foxhound/MRVP in protected role',
    limitation: 'No armor. Vulnerable to IEDs and small arms. PERMISSIVE only.',
    best_use: 'Low-threat rear area evacuation; being withdrawn from frontline'
  }
};

// ============================================================================
// FOXHOUND (Ocelot) CASEVAC - British Protected Patrol Vehicle with CASEVAC role
// ============================================================================

export const FOXHOUND_CASEVAC = {
  code: 'FOXHOUND_CASEVAC',
  name: 'Foxhound (Ocelot) CASEVAC',
  manufacturer: 'Force Protection Europe (UK)',
  nationality: 'British',
  icon: ICONS.AMBULANCE,
  category: 'PROTECTED',

  capacity: {
    stretchers: 2,
    ambulatory: 4,
    crew: 2,
    medic: 1
  },

  performance: {
    max_speed_kmh: 120,
    cruise_speed_kmh: 95,
    range_km: 700,
    fuel_consumption_l_per_100km: 18
  },

  armor_protection: 'STANAG_2',

  survivability: {
    stanag_level: 2,
    ied_survival_rate: 0.70,
    small_arms_protection: true,
    mine_protection: true,
    v_hull: true
  },

  medical_equipment: {
    oxygen_system: true,
    suction: true,
    monitoring: 'BASIC',
    climate_control: false,
    prolonged_care_capable: false
  },

  fielding_status: 'ACTIVE',

  educational: {
    key_point: 'British protected CASEVAC — high IED survivability due to V-hull design',
    advantage: 'Excellent mine protection; used extensively by British forces post-Afghanistan',
    best_use: 'Point of injury extraction in contested/IED-threat environments'
  }
};

// ============================================================================
// VAB MEDEVAC - French light armored ambulance (Véhicule de l'Avant Blindé)
// ============================================================================

export const VAB_MEDEVAC = {
  code: 'VAB_MEDEVAC',
  name: 'VAB MEDEVAC',
  manufacturer: 'Renault Trucks Defense (France)',
  nationality: 'French',
  icon: ICONS.AMBULANCE,
  category: 'ARMORED',

  capacity: {
    stretchers: 4,
    ambulatory: 6,
    crew: 2,
    medic: 2
  },

  performance: {
    max_speed_kmh: 92,
    cruise_speed_kmh: 75,
    range_km: 1000,
    fuel_consumption_l_per_100km: 40
  },

  armor_protection: 'STANAG_1',

  survivability: {
    stanag_level: 1,
    ied_survival_rate: 0.35,
    small_arms_protection: true,
    mine_protection: false
  },

  medical_equipment: {
    oxygen_system: true,
    suction: true,
    monitoring: 'STANDARD',
    climate_control: true,
    prolonged_care_capable: true
  },

  fielding_status: 'ACTIVE',

  educational: {
    key_point: 'French standard armored ambulance — wheeled 4x4, long operational range',
    advantage: '1000km range, amphibious capability, proven in French operations Africa/Sahel',
    limitation: 'Light armor only — not suitable for high-intensity contested environments',
    best_use: 'French Brigade-level MEDEVAC, Role 1 to Role 2 evacuation',
    doctrinal_note: 'French ALAT/Army doctrine TTA 400. Being supplemented by GRIFFON MEDEVAC variant.'
  }
};

// ============================================================================
// GRIFFON MEDEVAC - French next-generation armored ambulance (VBMR Griffon)
// ============================================================================

export const GRIFFON_MEDEVAC = {
  code: 'GRIFFON_MEDEVAC',
  name: 'Griffon MEDEVAC (VBMR)',
  manufacturer: 'Nexter / Arquus / Thales (France)',  // Scorpion consortium; Arquus = former Renault Trucks Defense (rebranded 2019)
  nationality: 'French',
  icon: ICONS.AMBULANCE,
  category: 'ARMORED',

  capacity: {
    stretchers: 4,
    ambulatory: 6,
    crew: 2,
    medic: 2
  },

  performance: {
    max_speed_kmh: 100,
    cruise_speed_kmh: 85,
    range_km: 800,
    fuel_consumption_l_per_100km: 38
  },

  armor_protection: 'STANAG_4',

  survivability: {
    stanag_level: 4,
    ied_survival_rate: 0.75,
    small_arms_protection: true,
    mine_protection: true
  },

  medical_equipment: {
    oxygen_system: true,
    suction: true,
    monitoring: 'ENHANCED',
    climate_control: true,
    prolonged_care_capable: true
  },

  fielding_status: 'FIELDING',

  educational: {
    key_point: 'French next-generation armored ambulance — part of Scorpion modernization program',
    advantage: 'STANAG 4 protection, digitized medical systems, compatible with Scorpion network',
    best_use: 'Modern French Brigade MEDEVAC — replacing VAB in high-intensity roles',
    doctrinal_note: 'Scorpion VBMR programme (Nexter/Arquus/Thales). DGA qualified 2022; first unit deliveries 2023. 196 SAN variants planned by 2030 per LPM 2024-2030. Presented at Eurosatory 2024. Carries up to 5 wounded. Replaces VAB Sanitaire (VAB SAN) as primary SSA armored ambulance. Source: DGA/Arquus/ArmyRecognition Eurosatory June 2024.'
  }
};

// ============================================================================
// AMBULANCE AGGREGATIONS
// ============================================================================

export const AMBULANCE_TYPES = {
  // Romanian
  TABC79:           TABC_79,
  MLVM:             MLVM_TAB77,
  PIRANHA_V_MEDEVAC: PIRANHA_V_MEDEVAC,  // BUG-155: Romanian armored ambulance
  // US
  M997:             M997_HMMWV,
  JLTV:             JLTV_AMBULANCE,
  MATV:             M_ATV_AMBULANCE,
  M113:             M113_AMBULANCE,
  STRYKER:          STRYKER_MEV,
  AMPV:             AMPV_MEV,
  // German / NATO multinational
  SPRINTER:         SPRINTER_AMBULANCE,
  BOXER:            BOXER_AMBULANCE,
  // Polish
  STAR266:          STAR_266_AMBULANCE,
  ROSOMAK_MEDIC:    ROSOMAK_MEDIC,
  // British
  LAND_ROVER_WOLF:  LAND_ROVER_WOLF,
  FOXHOUND_CASEVAC: FOXHOUND_CASEVAC,
  // French
  VAB_MEDEVAC:      VAB_MEDEVAC,
  GRIFFON_MEDEVAC:  GRIFFON_MEDEVAC
};

export const AMBULANCE_CATEGORIES = {
  LIGHT:         ['TABC79', 'M997', 'SPRINTER', 'STAR266', 'LAND_ROVER_WOLF'],
  PROTECTED:     ['JLTV', 'FOXHOUND_CASEVAC'],
  MRAP:          ['MATV'],
  ARMORED:       ['M113', 'MLVM', 'PIRANHA_V_MEDEVAC', 'STRYKER', 'BOXER', 'ROSOMAK_MEDIC', 'VAB_MEDEVAC'], // BUG-155: Piranha V added
  HEAVY_ARMORED: ['AMPV', 'GRIFFON_MEDEVAC']
};

export const AMBULANCE_BY_STATUS = {
  ACTIVE:   ['SPRINTER', 'JLTV', 'MATV', 'MLVM', 'PIRANHA_V_MEDEVAC', 'STRYKER', 'BOXER',
             'ROSOMAK_MEDIC', 'FOXHOUND_CASEVAC', 'VAB_MEDEVAC'], // BUG-155: Piranha V added
  LEGACY:   ['TABC79', 'M997', 'M113', 'STAR266', 'LAND_ROVER_WOLF'],
  FIELDING: ['AMPV', 'GRIFFON_MEDEVAC']
};

export const AMBULANCE_BY_NATIONALITY = {
  ROMANIAN:     ['TABC79', 'MLVM', 'PIRANHA_V_MEDEVAC'],  // BUG-155: Piranha V added
  US:           ['M997', 'JLTV', 'MATV', 'M113', 'STRYKER', 'AMPV'],
  GERMAN:       ['SPRINTER', 'BOXER'],
  POLISH:       ['STAR266', 'ROSOMAK_MEDIC'],
  BRITISH:      ['LAND_ROVER_WOLF', 'FOXHOUND_CASEVAC'],
  UK:           ['LAND_ROVER_WOLF', 'FOXHOUND_CASEVAC'],  // Alias
  FRENCH:       ['VAB_MEDEVAC', 'GRIFFON_MEDEVAC'],
  NATO:         ['SPRINTER', 'BOXER']                     // Generic NATO procurement
};

// ============================================================================
// DEFAULT GROUND FLEET BY NATIONAL STANDARD
// Brigade-level planning default fleet composition.
// Purpose: Tab3 3B.0 pre-populates the ambulance fleet table based on the
// force national standard. Adding a new nation requires only one entry here
// (plus AMBULANCE_BY_NATIONALITY above if new vehicles are needed).
//
// count = authorized strength for a standard brigade medical company
// available = typical operational availability (85–90% for active vehicles)
//
// To add Bulgaria: add a BULGARIAN entry here.
// To add Hungary:  add a HUNGARIAN entry here.
// Constants-only change — no calculator or UI code modifications required.
// ============================================================================

// ============================================================================
// DEFAULT GROUND FLEET BY NATIONAL STANDARD
// Brigade-level planning default fleet composition.
// Purpose: Tab3 3B.0 pre-populates the ambulance fleet table based on the
// force national standard. One entry per nation — constants-only change to
// add/modify a fleet. No calculator or UI code modifications required.
//
// count    = authorized TOE strength (On Strength)
// available = typical operational availability (~85-90% for active vehicles)
//
// !! MANDATORY — ADDING A NEW NATIONAL FLAG REQUIRES THIS FILE !!
// When adding a new national standard, THREE entries are required here:
//   1. New vehicle constants in AMBULANCE_TYPES (if platform not already present)
//   2. AMBULANCE_BY_NATIONALITY entry (for UI dropdown filtering)
//   3. GROUND_FLEET_BY_NATION entry below (triggers Tab3 3B.0 pre-population)
// Missing step 3 → Tab3 silently falls back to NATO defaults for the new nation.
// See docs/documentation/APP_RULES.md Section 15 for the full multi-file checklist.
//
// FUTURE TODO: Add R1_organic / R2B_company breakdown sub-tables per nation.
// Currently all assets are combined into one brigade-level pool.
// Romanian structure example (for future breakdown):
//   R1_organic: { TABC79: 6, MLVM: 6 }  — 3 bn × (2+2)
//   R2B_company: { TABC79: 8, MLVM: 4 } — brigade medical company
// ============================================================================

export const GROUND_FLEET_BY_NATION = {
  ROMANIAN: {
    // Source: confirmed Dr. Tatar Radu MD (March 6, 2026)
    // Structure: R1 organic = 3 bn × (2 TABC79 + 2 MLVM) = 6+6
    //            R2B medical company = ~8 TABC79 + 4 MLVM (~12 total)
    // Brigade total: TABC79=14, MLVM=10 → 24 authorized
    // Available ~85%: 12+9=21
    label: 'Romanian brigade — R1 organic (3 bn × 2+2) + R2B medical company (~12)',
    vehicles: {
      TABC79:            { count: 14, available: 12 },  // 6 R1 organic + 8 R2B company (light)
      MLVM:              { count: 10, available: 9  },  // 6 R1 organic + 4 R2B company (armored)
      PIRANHA_V_MEDEVAC: { count: 4,  available: 4  }   // BUG-155: armored forward MEDEVAC; in service Oct 2020+
    }
  },

  US: {
    // Source: US Army ABCT MTOE 2024 (AMPV replacing M113 in ABCTs — Army Recognition May 2024)
    // Using ABCT profile as US default — most relevant for NATO LSCO planning context.
    // IBCT profile differs: replace AMPV_MEV with additional JLTV (no tracked assets).
    // R1 organic: 3 bn × 2 JLTV = 6 (Forward Support Company medics)
    // R2B BSMC: ~6 JLTV FLA + 6 AMPV MEV (M1284) = 12 (Brigade Support Medical Company)
    // Brigade total: JLTV=12, AMPV=6 → 18 authorized
    label: 'US ABCT — BSB Brigade Support Medical Company (ABCT MTOE 2024)',
    vehicles: {
      JLTV: { count: 12, available: 10 }, // JLTV M1286 FLA — primary light ambulance
      AMPV: { count: 6,  available: 6  }  // M1284 MEV — replacing M113 in ABCTs
    }
  },

  GERMAN: {
    // Source: Bundeswehr — Lkw UVT replacing KrKw (IVECO Eurocargo, 294 vehicles 2023-2027,
    // BAAINBw July 2021). Boxer ambulance variant in armored units (Panzergrenadiere).
    // R1: 3 bn × 2 UVT = 6 light. R2B Sanitätskompanie: ~6 UVT + 4 Boxer = 10.
    // Brigade total: SPRINTER(UVT)=12, BOXER=4 → 16 authorized
    // Note: SPRINTER used as proxy for new Lkw UVT (same LIGHT category, similar specs)
    label: 'German Sanitätskompanie — UVT (replacing KrKw) + Boxer armored supplement',
    vehicles: {
      SPRINTER: { count: 12, available: 11 }, // Lkw UVT gl (IVECO) — light/unprotected
      BOXER:    { count: 4,  available: 4  }  // Boxer ambulance variant — armored units
    }
  },

  POLISH: {
    // Source: Polish Land Forces brigade medical company doctrine.
    // STAR 266 is the legacy light ambulance. Rosomak (KTO 8×8) has medical variant.
    // R1: 3 bn × 2 STAR266 = 6. R2B kompania medyczna: ~4 STAR266 + 6 Rosomak = 10.
    // Brigade total: STAR266=10, ROSOMAK=6 → 16 authorized
    label: 'Polish kompania medyczna — Star 266 (light) + Rosomak Medic (armored)',
    vehicles: {
      STAR266:       { count: 10, available: 8 }, // Star 266 — legacy light, still primary R1
      ROSOMAK_MEDIC: { count: 6,  available: 5 }  // KTO Rosomak Medic — modern R2B asset
    }
  },

  BRITISH: {
    // Source: British Army Medical Regiment / Regimental Medical Officer structure.
    // Land Rover Wolf (Pulse) being replaced by Foxhound (LPPV Force Protection).
    // Mixed fleet during transition. R1: 3 bn × 2 = 6. R2B: ~4 Wolf + 4 Foxhound = 8.
    // Brigade total: Wolf=6 (legacy R1), Foxhound=8 (new protected) → 14 authorized
    label: 'British Medical Regiment — Land Rover Wolf (legacy R1) + Foxhound (R2B, protected)',
    vehicles: {
      LAND_ROVER_WOLF:  { count: 6, available: 5 }, // Legacy — R1 use; being phased out
      FOXHOUND_CASEVAC: { count: 8, available: 7 }  // LPPV Foxhound — R2B protected
    }
  },

  FRENCH: {
    // Source: French Army SSA (Service de Santé des Armées) VBMR programme.
    // Griffon SAN (sanitaire) = armored ambulance variant of the VBMR Griffon 6×6.
    // DGA qualified 2022; first deliveries to SSA units 2023; 196 planned by 2030 (LPM 2024-2030).
    // VAB Sanitaire (VAB SAN) = legacy platform; still primary pending Griffon SAN full fielding.
    // R1: 3 bn × 2 VAB SAN = 6. R2B compagnie médicale: ~4 VAB SAN + 4 Griffon SAN = 8.
    // Brigade total: VAB=10, GRIFFON=4 → 14 authorized; transitional fleet (March 2026 fielding state)
    label: 'French compagnie médicale — VAB SAN (primary legacy) + Griffon SAN (fielding 2023+)',
    vehicles: {
      VAB_MEDEVAC:     { count: 10, available: 8 }, // VAB SAN — primary legacy; R1 + R2B
      GRIFFON_MEDEVAC: { count: 4,  available: 4 }  // Griffon SAN — modern R2B supplement; 196 total by 2030
    }
  },

  NATO: {
    // Generic NATO baseline — Lead Nation standard per AJP-4.10C.
    // Used when national standard is NATO or unknown. Sprinter = unprotected light;
    // Boxer = protected supplement for forward areas. No tracked assets assumed.
    // Brigade total: SPRINTER=10, BOXER=4 → 14 authorized
    label: 'NATO generic — Lead Nation standard (AJP-4.10C); configure per lead nation',
    vehicles: {
      SPRINTER: { count: 10, available: 9 }, // Light unprotected — rear and R2B
      BOXER:    { count: 4,  available: 4 }  // Protected — forward areas and contested env.
    }
  },

  MULTINATIONAL: {
    // Mixed fleet for multinational corps scenarios. Configure per actual lead nation
    // after Tab1 national standard selection. These are conservative planning defaults.
    // Brigade total: SPRINTER=8, BOXER=4 → 12 authorized (minimum planning figure)
    label: 'Multinational — mixed fleet; configure per-nation after Tab1 force composition',
    vehicles: {
      SPRINTER: { count: 8, available: 7 }, // Conservative light ambulance minimum
      BOXER:    { count: 4, available: 4 }  // Protected minimum for forward operations
    }
  }
};

/**
 * Get default ground fleet for a national standard.
 * Used by Tab3 3B.0 to pre-populate fleet table when national standard changes.
 * @param {string} nationalStandard - National standard key
 * @returns {{ vehicles: Object, label: string }}
 */
export const getDefaultGroundFleet = (nationalStandard) => {
  const key = nationalStandard?.toUpperCase();
  return GROUND_FLEET_BY_NATION[key] || GROUND_FLEET_BY_NATION.NATO;
};

// ============================================================================
// AMBULANCE RECOMMENDATIONS BY THREAT
// ============================================================================

export const AMBULANCE_RECOMMENDATIONS_BY_THREAT = {
  PERMISSIVE: {
    icon: ICONS.GREEN,
    recommended_types: ['SPRINTER', 'TABC79', 'M997'],
    notes: 'Light vehicles acceptable'
  },
  LOW: {
    icon: ICONS.GREEN,
    recommended_types: ['JLTV', 'MLVM', 'STRYKER'],
    notes: 'Protected vehicles preferred'
  },
  MEDIUM: {
    icon: ICONS.YELLOW,
    recommended_types: ['JLTV', 'MATV', 'STRYKER', 'BOXER'],
    notes: 'Armored vehicles required'
  },
  HIGH: {
    icon: ICONS.ORANGE,
    recommended_types: ['MATV', 'STRYKER', 'AMPV'],
    notes: 'Heavy armor required'
  },
  EXTREME: {
    icon: ICONS.RED,
    recommended_types: ['AMPV', 'STRYKER'],
    notes: 'Maximum protection required. Consider alternative routes.'
  }
};

// ============================================================================
// DEFAULT AMBULANCE ALLOCATION
// ============================================================================

export const DEFAULT_AMBULANCE_ALLOCATION = {
  ROMANIAN: {
    primary: 'MLVM',
    secondary: 'TABC79',
    forward: 'MLVM',
    rear: 'TABC79',
    icon: 'ðŸ‡·ðŸ‡´'
  },
  NATO: {
    primary: 'STRYKER',
    secondary: 'JLTV',
    forward: 'MATV',
    rear: 'SPRINTER',
    icon: 'ðŸŒ'
  },
  US: {
    primary: 'STRYKER',
    secondary: 'JLTV',
    forward: 'MATV',
    rear: 'M997',
    icon: 'ðŸ‡ºðŸ‡¸'
  }
};

// ============================================================================
// UI HELPER FUNCTIONS
// ============================================================================

/**
 * Get ambulance options for UI dropdown
 * @param {Object} filters - Optional filters
 * @returns {Array} Ambulance options
 */
export const getAmbulanceOptions = (filters = {}) => {
  let ambulances = Object.entries(AMBULANCE_TYPES);
  
  if (filters.category) {
    const categoryCodes = AMBULANCE_CATEGORIES[filters.category] || [];
    ambulances = ambulances.filter(([code]) => categoryCodes.includes(code));
  }
  
  if (filters.status) {
    const statusCodes = AMBULANCE_BY_STATUS[filters.status] || [];
    ambulances = ambulances.filter(([code]) => statusCodes.includes(code));
  }
  
  if (filters.nationality) {
    const nationalityCodes = AMBULANCE_BY_NATIONALITY[filters.nationality] || [];
    ambulances = ambulances.filter(([code]) => nationalityCodes.includes(code));
  }
  
  if (filters.excludeLegacy) {
    ambulances = ambulances.filter(([, spec]) => spec.fielding_status !== 'LEGACY');
  }
  
  return ambulances.map(([code, spec]) => ({
    value: code,
    label: `${spec.flag} ${spec.name}`,
    icon: spec.icon || ICONS.AMBULANCE,
    category: spec.category,
    capacity: spec.capacity.stretchers,
    protection: spec.armor_protection,
    stanag_level: spec.survivability.stanag_level,
    fielding_status: spec.fielding_status,
    prolonged_care: spec.medical_equipment.prolonged_care_capable,
    ied_survival: spec.survivability.ied_survival_rate
  }));
};

/**
 * Get category options for UI dropdown
 * @returns {Array} Category options
 */
export const getCategoryOptions = () => {
  return [
    { value: 'LIGHT', label: 'Light (No Armor)', icon: ICONS.YELLOW, stanagMax: 0 },
    { value: 'PROTECTED', label: 'Protected (JLTV)', icon: ICONS.GREEN, stanagMax: 4 },
    { value: 'MRAP', label: 'MRAP (IED Resistant)', icon: ICONS.GREEN, stanagMax: 4 },
    { value: 'ARMORED', label: 'Armored', icon: ICONS.GREEN, stanagMax: 4 },
    { value: 'HEAVY_ARMORED', label: 'Heavy Armored', icon: ICONS.GREEN, stanagMax: 5 }
  ];
};

/**
 * Get STANAG level options for UI dropdown
 * @returns {Array} STANAG level options
 */
export const getSTANAGLevelOptions = () => {
  return Object.entries(STANAG_PROTECTION_LEVELS).map(([key, level]) => ({
    value: level.level,
    label: `Level ${level.level}: ${level.kinetic_threat}`,
    icon: level.icon,
    description: level.description
  }));
};

/**
 * Get drone threat options for UI dropdown
 * @returns {Array} Drone threat options
 */
export const getDroneThreatOptions = () => {
  return Object.entries(DRONE_THREAT_MODIFIERS).map(([key, mod]) => ({
    value: key,
    label: mod.description,
    icon: mod.icon,
    speedModifier: mod.speed_modifier,
    daylightRisk: mod.daylight_risk
  }));
};

/**
 * Get road condition options for UI dropdown
 * @returns {Array} Road condition options
 */
export const getRoadConditionOptions = () => {
  return Object.entries(ROAD_CONDITION_MODIFIERS).map(([key, mod]) => ({
    value: key,
    label: mod.description,
    icon: mod.icon,
    speedModifier: mod.speed_modifier
  }));
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get ambulance specification by code
 * @param {string} code - Ambulance code
 * @returns {Object|null} Ambulance specification
 */
// Vehicle codes used by the ForceBuilder facility assets (evacuationAssetAllocation.js) and older saves, mapped to the
// AMBULANCE_TYPES keys. Before 2026-10-10 'TABC_79', 'M113_AMBULANCE', 'HMMWV_AMBULANCE' and 'PIRANHA_AMBULANCE' did
// not resolve, so the capacity data of these vehicles (patients, speed, range) was never reachable from the facilities.
// NATO_AMBULANCE is the generic NATO light ambulance of getRole1OrganicAssets: SPRINTER is the NATO 'rear' type in
// DEFAULT_AMBULANCE_ALLOCATION (PLACEHOLDER mapping, to be confirmed).
export const AMBULANCE_TYPE_ALIASES = {
  TABC_79: 'TABC79',
  M113_AMBULANCE: 'M113',
  HMMWV_AMBULANCE: 'M997',
  PIRANHA_AMBULANCE: 'PIRANHA_V_MEDEVAC',
  NATO_AMBULANCE: 'SPRINTER'
};

export const getAmbulanceSpec = (code) => {
  const key = code?.toUpperCase();
  return AMBULANCE_TYPES[key] || AMBULANCE_TYPES[AMBULANCE_TYPE_ALIASES[key]] || null;
};

/**
 * Get ambulance spec in calculation-friendly format
 * Adapts from detailed UI structure to simplified calculation structure
 * Used by medevacModule.js for capacity calculations
 * 
 * @param {string} code - Ambulance code (e.g., 'TABC_79', 'M113_AMBULANCE')
 * @returns {Object|null} Calculation-friendly spec or null if not found
 */
export const getAmbulanceCalcSpec = (code) => {
  const full = getAmbulanceSpec(code);
  if (!full) return null;
  
  // Category-based defaults for calculation-specific values.
  // These values are derived from operational data and doctrine.
  // ntm_minutes: F-04/ARCH-011 — crew alert + vehicle preparation time (mirrors GROUND_AMBULANCE_NTM).
  // Both tables must stay in sync. GROUND_AMBULANCE_NTM is the authoritative export;
  // this local copy avoids a circular-reference import inside the same module.
  const CATEGORY_DEFAULTS = {
    LIGHT: {
      typical_availability: 0.85,
      trips_per_day: 4,
      load_time_minutes: 5,
      unload_time_minutes: 3,
      terrain_capability: 'ROAD_IMPROVED',
      ntm_minutes: 5    // FM 4-02.4: unprotected wheeled — fast start, minimal PMCS
    },
    PROTECTED: {
      typical_availability: 0.80,
      trips_per_day: 3.5,
      load_time_minutes: 6,
      unload_time_minutes: 4,
      terrain_capability: 'ALL',
      ntm_minutes: 8    // FM 4-02.4: JLTV class — PMCS + pre-departure checks
    },
    MRAP: {
      typical_availability: 0.75,
      trips_per_day: 3,
      load_time_minutes: 6,
      unload_time_minutes: 4,
      terrain_capability: 'ALL',
      ntm_minutes: 8    // FM 4-02.4: same readiness class as PROTECTED
    },
    ARMORED: {
      typical_availability: 0.70,
      trips_per_day: 2.5,
      load_time_minutes: 8,
      unload_time_minutes: 5,
      terrain_capability: 'ALL',
      ntm_minutes: 12   // FM 4-02.4: tracked/armored wheeled — engine warm-up + pre-battle checks
    },
    HEAVY_ARMORED: {
      typical_availability: 0.72,
      trips_per_day: 2.5,
      load_time_minutes: 8,
      unload_time_minutes: 5,
      terrain_capability: 'ALL',
      ntm_minutes: 10   // FM 4-02.4: modern heavy tracked (AMPV) — electronics-assisted startup
    },
    WHEELED: {
      typical_availability: 0.85,
      trips_per_day: 4,
      load_time_minutes: 5,
      unload_time_minutes: 3,
      terrain_capability: 'ROAD_IMPROVED',
      ntm_minutes: 8    // Generic wheeled fallback
    },
    BUS: {
      typical_availability: 0.90,
      trips_per_day: 2,
      load_time_minutes: 15,
      unload_time_minutes: 10,
      terrain_capability: 'ROAD_ONLY',
      ntm_minutes: 5    // Non-combat vehicle — parking-lot ready
    }
  };
  
  const categoryDefaults = CATEGORY_DEFAULTS[full.category] || CATEGORY_DEFAULTS.WHEELED;
  
  // Map armor_protection to protection_level for calculation
  const PROTECTION_MAP = {
    'NONE': 'NONE',
    'STANAG_1': 'LOW',
    'STANAG_2': 'LOW',
    'STANAG_3': 'MEDIUM',
    'STANAG_4': 'HIGH',
    'STANAG_4+': 'HIGH'
  };
  
  return {
    // Identity
    code: full.code,
    label: full.name,
    category: full.category,
    nationality: full.nationality,
    
    // Capacity - mapped from nested structure
    litter_capacity: full.capacity?.stretchers || 0,
    ambulatory_capacity: full.capacity?.ambulatory || 0,
    mixed_litter: full.capacity?.mixed_stretchers || 0,
    mixed_ambulatory: full.capacity?.mixed_ambulatory || 0,
    crew: full.capacity?.crew || 2,
    
    // Performance - mapped from nested structure
    speed_km_h: full.performance?.cruise_speed_kmh || 60,
    max_speed_km_h: full.performance?.max_speed_kmh || 80,
    range_km: full.performance?.range_km || 400,
    
    // Calculation-specific values from category defaults
    typical_availability: categoryDefaults.typical_availability,
    trips_per_day: categoryDefaults.trips_per_day,
    load_time_minutes: categoryDefaults.load_time_minutes,
    unload_time_minutes: categoryDefaults.unload_time_minutes,
    terrain_capability: categoryDefaults.terrain_capability,
    // F-04/ARCH-011: NTM inherited from category — no per-vehicle field needed.
    // When Romania switches from TABC-79 to Piranha (ARMORED), NTM = 12 automatically.
    ntm_minutes: categoryDefaults.ntm_minutes,
    
    // Protection
    protection_level: PROTECTION_MAP[full.armor_protection] || 'NONE',
    stanag_level: full.survivability?.stanag_level || 0,
    
    // Status
    fielding_status: full.fielding_status || 'CURRENT',
    is_legacy: full.fielding_status === 'LEGACY',
    
    // Medical capability
    prolonged_care_capable: full.medical_equipment?.prolonged_care_capable || false,
    monitoring_level: full.medical_equipment?.monitoring || 'BASIC',
    
    // Educational
    limitation: full.educational?.limitation || null,
    recommendation: full.educational?.recommendation || null
  };
};

/**
 * Get all ambulance calc specs as an object (for use as AMBULANCE_TYPE_SPECS replacement)
 * @returns {Object} Map of code to calc spec
 */
export const getAllAmbulanceCalcSpecs = () => {
  const specs = {};
  for (const code of Object.keys(AMBULANCE_TYPES)) {
    specs[code] = getAmbulanceCalcSpec(code);
  }
  return specs;
};

/**
 * Get ambulances by category
 * @param {string} category - Category name
 * @returns {Array} Ambulance specifications
 */
export const getAmbulancesByCategory = (category) => {
  const codes = AMBULANCE_CATEGORIES[category] || [];
  return codes.map(code => AMBULANCE_TYPES[code]).filter(Boolean);
};

/**
 * Get ambulances by status
 * @param {string} status - Status name
 * @returns {Array} Ambulance specifications
 */
export const getAmbulancesByStatus = (status) => {
  const codes = AMBULANCE_BY_STATUS[status] || [];
  return codes.map(code => AMBULANCE_TYPES[code]).filter(Boolean);
};

/**
 * Get ambulances by nationality
 * @param {string} nationality - Nationality
 * @returns {Array} Ambulance specifications
 */
export const getAmbulancesByNationality = (nationality) => {
  const codes = AMBULANCE_BY_NATIONALITY[nationality] || [];
  return codes.map(code => AMBULANCE_TYPES[code]).filter(Boolean);
};

/**
 * Get default ambulance allocation for national standard
 * @param {string} nationalStandard - National standard
 * @returns {Object} Default allocation
 */
export const getDefaultAmbulanceAllocation = (nationalStandard) => {
  return DEFAULT_AMBULANCE_ALLOCATION[nationalStandard?.toUpperCase()] || 
         DEFAULT_AMBULANCE_ALLOCATION.NATO;
};

/**
 * Get ambulance recommendation for threat level
 * @param {string} threatLevel - Threat level
 * @returns {Object} Recommendation
 */
export const getAmbulanceRecommendation = (threatLevel) => {
  return AMBULANCE_RECOMMENDATIONS_BY_THREAT[threatLevel?.toUpperCase()] ||
         AMBULANCE_RECOMMENDATIONS_BY_THREAT.MEDIUM;
};

/**
 * Get STANAG level for ambulance
 * @param {string} ambulanceCode - Ambulance code
 * @returns {Object} STANAG level details
 */
export const getSTANAGLevel = (ambulanceCode) => {
  const spec = getAmbulanceSpec(ambulanceCode);
  if (!spec) return null;
  
  const level = spec.survivability.stanag_level;
  return STANAG_PROTECTION_LEVELS[`LEVEL_${level}`] || STANAG_PROTECTION_LEVELS.LEVEL_0;
};

/**
 * Calculate evacuation capacity
 * @param {string} ambulanceCode - Ambulance code
 * @param {number} distanceKm - Distance in km
 * @param {Object} environment - Environmental factors
 * @returns {Object} Capacity calculation
 */
export const calculateEvacuationCapacity = (ambulanceCode, distanceKm, environment = {}) => {
  const spec = getAmbulanceSpec(ambulanceCode);
  if (!spec) return null;
  
  const {
    roadCondition = 'PAVED_GOOD',
    droneThreat = 'MEDIUM',
    nightOps = false
  } = environment;
  
  // Get modifiers
  const roadMod = ROAD_CONDITION_MODIFIERS[roadCondition] || ROAD_CONDITION_MODIFIERS.PAVED_GOOD;
  const droneMod = DRONE_THREAT_MODIFIERS[droneThreat] || DRONE_THREAT_MODIFIERS.MEDIUM;
  const nightMod = nightOps ? NIGHT_OPERATIONS_MODIFIERS[spec.category] || 
                             NIGHT_OPERATIONS_MODIFIERS.LIGHT : { speed_modifier: 1.0 };
  
  // Calculate effective speed
  const baseSpeed = spec.performance.cruise_speed_kmh;
  const effectiveSpeed = baseSpeed * roadMod.speed_modifier * droneMod.speed_modifier * nightMod.speed_modifier;
  
  // Calculate round trip time
  const roundTripKm = distanceKm * 2 * roadMod.route_modifier * droneMod.route_modifier;
  const loadingMinutes = 10;
  const unloadingMinutes = 5;
  const roundTripMinutes = (roundTripKm / effectiveSpeed) * 60 + loadingMinutes + unloadingMinutes;
  
  // Calculate daily capacity
  const operatingHours = nightOps ? 12 : 10;
  const tripsPerDay = Math.floor((operatingHours * 60) / roundTripMinutes);
  const patientsPerDay = tripsPerDay * spec.capacity.stretchers;
  
  // Golden hour calculation
  const oneWayMinutes = (distanceKm * roadMod.route_modifier / effectiveSpeed) * 60;
  const goldenHourAchievable = oneWayMinutes <= 60;
  
  // Determine status icon
  let icon = ICONS.GREEN;
  if (!goldenHourAchievable) icon = ICONS.ORANGE;
  if (patientsPerDay < spec.capacity.stretchers * 2) icon = ICONS.RED;
  
  return {
    ambulance: spec.name,
    ambulance_code: ambulanceCode,
    distance_km: distanceKm,
    effective_speed_kmh: Math.round(effectiveSpeed),
    round_trip_minutes: Math.round(roundTripMinutes),
    trips_per_day: tripsPerDay,
    patients_per_day: patientsPerDay,
    golden_hour_achievable: goldenHourAchievable,
    golden_hour_one_way_minutes: Math.round(oneWayMinutes),
    icon
  };
};

/**
 * Calculate fleet capacity
 * @param {Object} fleet - Fleet composition { code: count }
 * @param {number} distanceKm - Distance in km
 * @param {Object} environment - Environmental factors
 * @returns {Object} Fleet capacity
 */
export const calculateFleetCapacity = (fleet, distanceKm, environment = {}) => {
  let totalCapacity = 0;
  const breakdown = [];
  const warnings = [];
  let hasLegacyVehicles = false;
  
  Object.entries(fleet).forEach(([code, count]) => {
    if (count > 0) {
      const capacity = calculateEvacuationCapacity(code, distanceKm, environment);
      
      if (capacity) {
        const fleetCapacity = capacity.patients_per_day * count;
        totalCapacity += fleetCapacity;
        
        const spec = getAmbulanceSpec(code);
        if (spec?.fielding_status === 'LEGACY') {
          hasLegacyVehicles = true;
          warnings.push({
            severity: 'WARNING',
            code: 'LEGACY_VEHICLE',
            message: `${spec.name} is LEGACY - consider replacement`,
            icon: ICONS.YELLOW
          });
        }
        
        breakdown.push({
          type: code,
          name: capacity.ambulance,
          count: count,
          per_vehicle: capacity.patients_per_day,
          total: fleetCapacity,
          golden_hour_achievable: capacity.golden_hour_achievable,
          icon: capacity.icon
        });
      }
    }
  });
  
  // Sort by capacity contribution
  breakdown.sort((a, b) => b.total - a.total);
  
  // Determine overall status
  let icon = ICONS.GREEN;
  if (warnings.some(w => w.severity === 'CRITICAL')) {
    icon = ICONS.RED;
  } else if (hasLegacyVehicles || warnings.length > 0) {
    icon = ICONS.YELLOW;
  }
  
  return {
    total_patients_per_day: totalCapacity,
    vehicle_count: Object.values(fleet).reduce((a, b) => a + b, 0),
    breakdown,
    warnings,
    warning_count: warnings.length,
    has_legacy_vehicles: hasLegacyVehicles,
    icon
  };
};

/**
 * Get drone threat modifier
 * @param {string} level - Drone threat level
 * @returns {Object} Drone threat modifier
 */
export const getDroneThreatModifier = (level) => {
  return DRONE_THREAT_MODIFIERS[level] || DRONE_THREAT_MODIFIERS.MEDIUM;
};

/**
 * Get night operations modifier for vehicle category
 * @param {string} category - Vehicle category
 * @returns {Object} Night operations modifier
 */
export const getNightOperationsModifier = (category) => {
  return NIGHT_OPERATIONS_MODIFIERS[category] || NIGHT_OPERATIONS_MODIFIERS.LIGHT;
};

/**
 * Get required medical capability for evacuation time
 * @param {number} evacuationTimeMinutes - Evacuation time in minutes
 * @returns {Object} Required capability level
 */
export const getRequiredMedicalCapability = (evacuationTimeMinutes) => {
  const effects = EXTENDED_EVACUATION_EFFECTS.required_capability;
  
  if (evacuationTimeMinutes <= 30) return effects.under_30min;
  if (evacuationTimeMinutes <= 60) return effects.under_1h;
  if (evacuationTimeMinutes <= 120) return effects['1h_to_2h'];
  if (evacuationTimeMinutes <= 240) return effects['2h_to_4h'];
  return effects.over_4h;
};

/**
 * Calculate additional mortality for extended evacuation
 * @param {string} triageCategory - Triage category
 * @param {number} hoursDelayed - Hours beyond golden hour
 * @returns {number} Additional mortality rate
 */
export const calculateExtendedEvacuationMortality = (triageCategory, hoursDelayed) => {
  if (hoursDelayed <= 0) return 0;
  
  const rate = EXTENDED_EVACUATION_EFFECTS.mortality_increase_per_hour[triageCategory] || 0;
  return Math.min(rate * hoursDelayed, 0.50);
};

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate ambulance configuration
 * @param {Object} config - Configuration to validate
 * @returns {Object} Validation result
 */
export const validateAmbulanceConfig = (config) => {
  const errors = [];
  const warnings = [];
  
  // Validate ambulance code
  if (config.ambulanceCode && !getAmbulanceSpec(config.ambulanceCode)) {
    errors.push({
      field: 'ambulanceCode',
      message: `Invalid ambulance code: ${config.ambulanceCode}`,
      icon: ICONS.RED
    });
  }
  
  // Check for light vehicles in high threat
  if (config.ambulanceCode && config.threatLevel) {
    const spec = getAmbulanceSpec(config.ambulanceCode);
    if (spec?.category === 'LIGHT' && ['HIGH', 'EXTREME'].includes(config.threatLevel)) {
      warnings.push({
        field: 'ambulanceCode',
        message: `${spec.name} is LIGHT category - not recommended for ${config.threatLevel} threat`,
        icon: ICONS.WARNING
      });
    }
  }
  
  // Check for legacy vehicles
  if (config.ambulanceCode) {
    const spec = getAmbulanceSpec(config.ambulanceCode);
    if (spec?.fielding_status === 'LEGACY') {
      warnings.push({
        field: 'ambulanceCode',
        message: `${spec.name} is LEGACY status - plan for replacement`,
        icon: ICONS.YELLOW
      });
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
    icon: errors.length > 0 ? ICONS.RED : (warnings.length > 0 ? ICONS.YELLOW : ICONS.GREEN)
  };
};

/**
 * Check if ambulance code is valid
 * @param {string} code - Ambulance code
 * @returns {boolean} True if valid
 */
export const isValidAmbulanceCode = (code) => {
  return code in AMBULANCE_TYPES;
};

/**
 * Check if category is valid
 * @param {string} category - Category name
 * @returns {boolean} True if valid
 */
export const isValidCategory = (category) => {
  return category in AMBULANCE_CATEGORIES;
};

/**
 * Get all valid ambulance codes
 * @returns {Array} Valid codes
 */
export const getValidAmbulanceCodes = () => {
  return Object.keys(AMBULANCE_TYPES);
};

/**
 * Get all valid categories
 * @returns {Array} Valid categories
 */
export const getValidCategories = () => {
  return Object.keys(AMBULANCE_CATEGORIES);
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named default export (fixes lint warning)
const ambulanceTypesConstants = {
  // Individual vehicle specs
  TABC_79,
  M997_HMMWV,
  SPRINTER_AMBULANCE,
  JLTV_AMBULANCE,
  M_ATV_AMBULANCE,
  M113_AMBULANCE,
  MLVM_TAB77,
  STRYKER_MEV,
  AMPV_MEV,
  BOXER_AMBULANCE,
  // Phase 5 additions — Polish, British, French
  STAR_266_AMBULANCE,
  ROSOMAK_MEDIC,
  LAND_ROVER_WOLF,
  FOXHOUND_CASEVAC,
  VAB_MEDEVAC,
  GRIFFON_MEDEVAC,

  // Aggregated
  AMBULANCE_TYPES,
  AMBULANCE_CATEGORIES,
  AMBULANCE_BY_STATUS,
  AMBULANCE_BY_NATIONALITY,
  GROUND_FLEET_BY_NATION,
  DEFAULT_AMBULANCE_ALLOCATION,
  AMBULANCE_RECOMMENDATIONS_BY_THREAT,

  // Modifiers
  STANAG_PROTECTION_LEVELS,
  DRONE_THREAT_MODIFIERS,
  NIGHT_OPERATIONS_MODIFIERS,
  EXTENDED_EVACUATION_EFFECTS,
  ROAD_CONDITION_MODIFIERS,
  WEATHER_MODIFIERS,
  CONVOY_TACTICAL_SPEED_KMH,
  GROUND_AMBULANCE_NTM,
  
  // UI Options
  getAmbulanceOptions,
  getCategoryOptions,
  getSTANAGLevelOptions,
  getDroneThreatOptions,
  getRoadConditionOptions,
  
  // Helper functions
  getAmbulanceSpec,
  getAmbulancesByCategory,
  getAmbulancesByStatus,
  getAmbulancesByNationality,
  getDefaultAmbulanceAllocation,
  getDefaultGroundFleet,
  getAmbulanceRecommendation,
  getSTANAGLevel,
  calculateEvacuationCapacity,
  calculateFleetCapacity,
  getDroneThreatModifier,
  getNightOperationsModifier,
  getRequiredMedicalCapability,
  calculateExtendedEvacuationMortality,
  
  // Validation
  validateAmbulanceConfig,
  isValidAmbulanceCode,
  isValidCategory,
  getValidAmbulanceCodes,
  getValidCategories
};

export default ambulanceTypesConstants;
